import { useCallback, useEffect, useMemo, useState } from 'react'

import { HotelPricingRates } from '@monorepo/functions/src/types/config'

import { PricingPeriodEditorProvider } from '@/shared/providers/PricingPeriodEditorProvider'
import { PricingPeriodProvider, usePricingPeriods } from '@/shared/providers/PricingPeriodProvider'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import {
    findOverlappingPeriod,
    formatPeriodHeadline,
    generatePeriodId,
    getMostRecentPeriod,
    shiftPeriodDatesByOneYear,
    validatePeriodDateRange,
} from '@/shared/utils/pricing-periods'

import { HotelPricingForm } from './hotel-pricing-form'
import { HotelHoursCard } from './hotel-hours-card'

function PricingPeriodsContent() {
    const { periods, loading, addPeriod, updatePeriod, removePeriod, currentPeriod } = usePricingPeriods()
    const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null)
    const [draftDates, setDraftDates] = useState<{ startDate: string; endDate: string } | null>(null)

    const selectedPeriod = useMemo(
        () => periods.find(period => period.id === selectedPeriodId) ?? null,
        [periods, selectedPeriodId],
    )

    useEffect(() => {
        if (selectedPeriod) {
            setDraftDates({ startDate: selectedPeriod.startDate, endDate: selectedPeriod.endDate })
        }
    }, [selectedPeriod?.id, selectedPeriod?.startDate, selectedPeriod?.endDate])

    const dateValidation = useMemo(
        () => (draftDates ? validatePeriodDateRange(draftDates.startDate, draftDates.endDate) : null),
        [draftDates],
    )

    const overlap = useMemo(() => {
        if (!selectedPeriod || !draftDates || !dateValidation?.valid) return null
        return findOverlappingPeriod({ ...draftDates, id: selectedPeriod.id }, periods)
    }, [dateValidation?.valid, draftDates, periods, selectedPeriod])

    const persistDatesIfValid = useCallback(
        async (dates: { startDate: string; endDate: string }) => {
            if (!selectedPeriod) return

            const validation = validatePeriodDateRange(dates.startDate, dates.endDate)
            if (!validation.valid) return
            if (findOverlappingPeriod({ ...dates, id: selectedPeriod.id }, periods)) return

            const name = formatPeriodHeadline(dates.startDate, dates.endDate)
            if (
                selectedPeriod.startDate === dates.startDate &&
                selectedPeriod.endDate === dates.endDate &&
                selectedPeriod.name === name
            ) {
                return
            }

            await updatePeriod(selectedPeriod.id, { ...dates, name })
        },
        [periods, selectedPeriod, updatePeriod],
    )

    const handleSelectPeriod = (periodId: string) => {
        const period = periods.find(p => p.id === periodId)
        setSelectedPeriodId(periodId)
        if (period) {
            setDraftDates({ startDate: period.startDate, endDate: period.endDate })
        }
    }

    const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
        if (!draftDates) return
        const next = { ...draftDates, [field]: value }
        setDraftDates(next)
        void persistDatesIfValid(next)
    }

    const handleAddPeriod = async () => {
        const source = getMostRecentPeriod(periods)
        if (!source) return

        const shiftedDates = shiftPeriodDatesByOneYear(source)
        const id = generatePeriodId(shiftedDates.startDate, shiftedDates.endDate)
        const name = formatPeriodHeadline(shiftedDates.startDate, shiftedDates.endDate)
        const { id: _id, name: _name, startDate: _start, endDate: _end, ...rates } = source

        const newPeriod: HotelPricingRates = {
            id,
            name,
            ...shiftedDates,
            ...rates,
        }

        await addPeriod(newPeriod)
        setSelectedPeriodId(id)
        setDraftDates(shiftedDates)
    }

    if (loading) {
        return <p className='text-sm text-muted-foreground'>Cargando periodos de tarifa...</p>
    }

    return (
        <div className='grid gap-6 lg:grid-cols-[320px_1fr]'>
            <div className='space-y-6'>
                <HotelHoursCard />
                <Card>
                    <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4'>
                        <CardTitle className='text-lg'>Periodos</CardTitle>
                        <Button size='sm' onClick={handleAddPeriod} disabled={!periods.length}>
                            Añadir periodo
                        </Button>
                    </CardHeader>
                    <CardContent className='space-y-2'>
                        {periods.length === 0 && (
                            <p className='text-sm text-muted-foreground'>
                                No hay periodos configurados. Ejecuta la migración para crear el periodo inicial.
                            </p>
                        )}
                        {periods.map(period => {
                            const isCurrent = currentPeriod?.id === period.id
                            const isSelected = selectedPeriodId === period.id
                            return (
                                <button
                                    key={period.id}
                                    type='button'
                                    onClick={() => handleSelectPeriod(period.id)}
                                    className={`w-full rounded-lg border p-3 text-left text-sm transition ${
                                        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                                    }`}
                                >
                                    <div className='font-medium'>
                                        {formatPeriodHeadline(period.startDate, period.endDate)}
                                    </div>
                                    {isCurrent && (
                                        <div className='mt-1 text-xs text-primary'>Periodo vigente</div>
                                    )}
                                </button>
                            )
                        })}
                    </CardContent>
                </Card>
            </div>

            {selectedPeriod && draftDates ? (
                <Card>
                    <CardHeader className='space-y-4 pb-4'>
                        <CardTitle className='text-lg'>
                            {formatPeriodHeadline(draftDates.startDate, draftDates.endDate)}
                        </CardTitle>
                        <div className='grid gap-4 md:grid-cols-2'>
                            <div className='space-y-2'>
                                <Label>Fecha inicio</Label>
                                <Input
                                    type='date'
                                    value={draftDates.startDate}
                                    onChange={e => handleDateChange('startDate', e.target.value)}
                                />
                            </div>
                            <div className='space-y-2'>
                                <Label>Fecha fin</Label>
                                <Input
                                    type='date'
                                    value={draftDates.endDate}
                                    onChange={e => handleDateChange('endDate', e.target.value)}
                                />
                            </div>
                        </div>
                        {dateValidation && !dateValidation.valid && (
                            <p className='text-sm text-destructive'>{dateValidation.error}</p>
                        )}
                        {overlap && (
                            <p className='text-sm text-destructive'>
                                Solapa con el periodo {formatPeriodHeadline(overlap.startDate, overlap.endDate)}
                            </p>
                        )}
                        {periods.length > 1 && (
                            <div className='flex flex-wrap gap-2'>
                                <Button
                                    size='sm'
                                    variant='destructive'
                                    onClick={async () => {
                                        await removePeriod(selectedPeriod.id)
                                        setSelectedPeriodId(null)
                                        setDraftDates(null)
                                    }}
                                >
                                    Eliminar periodo
                                </Button>
                            </div>
                        )}
                    </CardHeader>
                    <CardContent className='border-t pt-6'>
                        <PricingPeriodEditorProvider periodId={selectedPeriod.id}>
                            <HotelPricingForm key={selectedPeriod.id} />
                        </PricingPeriodEditorProvider>
                    </CardContent>
                </Card>
            ) : (
                <div className='flex min-h-[240px] items-center justify-center rounded-lg border border-dashed p-8 text-sm text-muted-foreground'>
                    Selecciona un periodo para editar sus tarifas
                </div>
            )}
        </div>
    )
}

export function PricingPeriodsTab() {
    return (
        <PricingPeriodProvider activeOnly={false}>
            <PricingPeriodsContent />
        </PricingPeriodProvider>
    )
}
